import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { AnimatedDiv } from "@/components/motion";
import { IMAGES } from "@/components/home/images";

const storyParagraphs = [
  "KF Prints is built on a legacy of over 75 years in the textile industry, rooted in Surat — one of the world's leading textile hubs.",
  "Over the decades, we have evolved from traditional textile trading into a fully integrated manufacturing ecosystem, offering a wide range of fabrics and garments to both domestic and international markets.",
  "Our strength lies in combining deep industry experience with modern production capabilities, enabling us to consistently deliver quality, scale, and reliability.",
  "At KF Prints, we believe in long-term partnerships, continuous innovation, and creating value through every meter of fabric we produce.",
];

const values = [
  { title: "Heritage", desc: "75+ years of accumulated textile wisdom passed through generations." },
  { title: "Innovation", desc: "Modern machinery and techniques that push the boundaries of what's possible." },
  { title: "Reliability", desc: "Consistent quality and timely delivery that our partners count on." },
];

const galleryImages = [
  { src: IMAGES.product1, alt: "Fabric", extraCls: "" },
  { src: IMAGES.machinery, alt: "Machinery", extraCls: "" },
  { src: IMAGES.craftsmanship, alt: "Craftsmanship", extraCls: "hidden lg:block" },
];

const AboutHero = () => (
  <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.factory} alt="Factory interior" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/50" />
    </div>
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
      <div className="section-divider mb-6" />
      <h1 className="text-5xl md:text-7xl font-serif text-white tracking-tight">About KF Prints</h1>
    </div>
  </section>
);

const AboutStory = () => (
  <section className="py-24 lg:py-32 bg-white">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      {storyParagraphs.map((text, i) => (
        <AnimatedDiv key={text}>
          <p className={`text-base md:text-lg font-sans text-muted-foreground leading-relaxed ${i < storyParagraphs.length - 1 ? "mb-8" : ""}`}>
            {text}
          </p>
        </AnimatedDiv>
      ))}
    </div>
  </section>
);

const AboutValues = () => (
  <section className="py-24 lg:py-32 bg-background">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <AnimatedDiv className="mb-16">
        <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">Our Values</p>
        <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-foreground">What Drives Us</h2>
      </AnimatedDiv>
      <div className="grid md:grid-cols-3 gap-8">
        {values.map((v) => (
          <AnimatedDiv key={v.title} className="p-10 border border-border/50 bg-white">
            <h3 className="font-serif text-2xl text-foreground mb-4">{v.title}</h3>
            <p className="font-sans text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
          </AnimatedDiv>
        ))}
      </div>
    </div>
  </section>
);

const AboutGallery = () => (
  <section className="py-24 lg:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {galleryImages.map((img) => (
          <div key={img.alt} className={`aspect-[3/4] overflow-hidden ${img.extraCls}`}>
            <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="about-page" className="pt-20 lg:pt-24">
      <SEO
        title="About KF Prints | 75+ Years of Textile Manufacturing Heritage in Surat, India"
        description="KF Prints Pvt. Ltd. — a 75-year textile legacy from Surat, India. Discover our story, our brands Krish Fashion & IOLY, and our commitment to quality fabric manufacturing for global markets."
        keywords="about KF Prints, textile company Surat, Indian textile manufacturer history, fabric manufacturer Gujarat"
        path="/about"
      />
      <AboutHero />
      <AboutStory />
      <AboutValues />
      <AboutGallery />
    </div>
  );
}
